import { statusFilterToActive } from 'src/app/shared/utilities/status-filter';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl, FormGroup } from '@angular/forms';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';
import { IFilial, IListByFilterFiliais } from '../data/filiais';
import { CompanyService } from '../../companies/services/company.service';
import { ICompany } from '../../companies/models/company.interface';
import { FiliaisService } from '../services/filiais.service';
@Component({
  selector: 'app-filiais',
  templateUrl: './filiais.component.html',
  styleUrls: ['./filiais.component.scss']
})
export class FiliaisComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  private readonly destroy$ = new Subject<void>();
  private filiaisSub?: Subscription;
  acaoInput = new UntypedFormControl();
  statusControl = new UntypedFormControl('all');
  ordenarControl = new UntypedFormControl(1);
  /** Filtro por empresa para quem enxerga mais de uma (interno Oliv-e ou parceiro). */
  companyControl = new UntypedFormControl(null);
  companies: ICompany[] = [];
  filiais: IFilial[] = [];
  opcoesOrdenacao = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: EOrdenacao.MaisNovos,
      label: 'Mais novos'
    },
    {
      value: EOrdenacao.MaisAntigos,
      label: 'Mais antigos'
    }
  ];

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;

  editarFilialId?: string;

  constructor(
    private readonly filiaisservice: FiliaisService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyId = this.sessionService.getSession().companyId;
    this.carregarFiliais();

    if (!this.companyId) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntil(this.destroy$))
        .subscribe((resposta) => (this.companies = resposta.data ?? []));
    }

    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });

    this.ordenarControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.recarregarDoInicio();
    });

    this.statusControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.filiaisSub?.unsubscribe();
  }

  filtrarFilial(): void {
    this.recarregarDoInicio();
  }

  limparFiltros(): void {
    this.acaoInput.reset();
    this.statusControl.reset('all', { emitEvent: false });
    this.companyControl.reset(null, { emitEvent: false });
    this.recarregarDoInicio();
  }

  carregarFiliais(): void {
    const filtros = this.filterFiliais();

    // Filtros trocados em sequência: cancela a busca anterior para a resposta antiga não sobrescrever a nova.
    this.filiaisSub?.unsubscribe();
    this.filiaisSub = this.filiaisservice.listarTodasFiliais(filtros).subscribe({
      next: (res) => {
        this.filiais = res.data;
        this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
        this.dadosPaginacao.itensTotal = res.count!;
      },
      // Falhou: não deixa na tela a lista da busca anterior como se fosse o resultado dos filtros atuais.
      error: (erro) => {
        this.filiais = [];
        this.dadosPaginacao.totalPaginas = 0;
        this.dadosPaginacao.itensTotal = 0;
        if (!interceptorAvisouErro(erro)) {
          this.toastService.toast('Erro ao carregar as filiais. Tente novamente.', EMessageType.Error);
        }
      }
    });
  }

  /** Mudou um filtro, a ordenação ou o tamanho da página: a página atual pode não existir mais. */
  private recarregarDoInicio(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.carregarFiliais();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;

    this.carregarFiliais();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;

    this.recarregarDoInicio();
  }

  public downloadExcel(): void {
    const filtros = this.filterFiliais();

    this.filiaisservice.exportarExcel(filtros);
  }

  editarFilial(id: string): void {
    this.editarFilialId = id;
  }

  filterFiliais(): IListByFilterFiliais {
    const filtros: IListByFilterFiliais = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    const partnerId = this.sessionService.getSession().partnerId;
    // A empresa da sessão prevalece; o filtro só vale para quem não está preso a uma empresa.
    const companyId = this.sessionService.getSession().companyId || this.companyControl.value;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    filtros.active = statusFilterToActive(this.statusControl.value);

    if (this.acaoInput.value) {
      filtros.searchText = String(this.acaoInput.value);
    }

    return filtros;
  }
}
